import {
  InventoryItem,
  InventoryTransaction,
  InventorySummary,
  StockAdjustment,
} from "../types/inventory";

import apiClient from "./api";

class InventoryService {
  /**
   * Get all inventory items with optional filtering
   */
  async getInventoryItems(
    params: {
      skip?: number;
      limit?: number;
      search?: string;
      include_inactive?: boolean;
    } = {},
  ): Promise<InventoryItem[]> {
    return await apiClient.get<InventoryItem[]>("/inventory/ingredients", {
      params,
    });
  }

  /**
   * Get inventory item by ID
   */
  async getInventoryItemById(id: number): Promise<InventoryItem> {
    return await apiClient.get<InventoryItem>(`/inventory/ingredients/${id}`);
  }

  /**
   * Create a new inventory item
   */
  async createInventoryItem(itemData: {
    name: string;
    code?: string;
    description?: string;
    unit: string;
    minimum_stock?: number;
    cost_per_unit?: number;
    supplier?: string;
    current_stock?: number;
  }): Promise<InventoryItem> {
    return await apiClient.post<InventoryItem>(
      "/inventory/ingredients",
      itemData,
    );
  }

  /**
   * Update an existing inventory item
   */
  async updateInventoryItem(
    id: number,
    itemData: {
      name?: string;
      code?: string;
      description?: string;
      unit?: string;
      minimum_stock?: number;
      cost_per_unit?: number;
      supplier?: string;
      is_active?: boolean;
    },
  ): Promise<InventoryItem> {
    return await apiClient.put<InventoryItem>(
      `/inventory/ingredients/${id}`,
      itemData,
    );
  }

  /**
   * Update inventory item stock level
   */
  async updateItemStock(
    id: number,
    currentStock: number,
    reason: string,
  ): Promise<InventoryItem> {
    return await apiClient.put<InventoryItem>(
      `/inventory/ingredients/${id}/stock?current_stock=${currentStock}&reason=${encodeURIComponent(reason)}`,
    );
  }

  /**
   * Get inventory transactions with filtering options
   */
  async getInventoryTransactions(
    params: {
      skip?: number;
      limit?: number;
      ingredient_id?: number;
      transaction_type?: string;
      start_date?: string;
      end_date?: string;
    } = {},
  ): Promise<InventoryTransaction[]> {
    return await apiClient.get<InventoryTransaction[]>(
      "/inventory/transactions",
      { params },
    );
  }

  /**
   * Create a stock adjustment with multiple transactions
   */
  async createStockAdjustment(adjustmentData: {
    reason: string;
    notes?: string;
    transactions: {
      ingredient_id: number;
      transaction_type: string;
      quantity: number;
      unit_cost?: number;
      reference_id?: number;
      reference_type?: string;
      notes?: string;
    }[];
  }): Promise<StockAdjustment> {
    return await apiClient.post<StockAdjustment>(
      "/inventory/stock-adjustment",
      adjustmentData,
    );
  }

  /**
   * Get inventory forecast
   */
  async getInventoryForecast(days: number = 30): Promise<any> {
    return await apiClient.get<any>(`/inventory/forecast?days=${days}`);
  }

  /**
   * Get inventory summary statistics
   */
  async getInventorySummary(): Promise<InventorySummary> {
    return await apiClient.get<InventorySummary>("/inventory/summary");
  }
}

// Create singleton instance
export const inventoryService = new InventoryService();
